import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';

import * as ZonesActions from '../actions/zones.actions';
import { getSelectionDialogId, getZoneDialogId, getZonesTableState } from '../selectors/zones.selectors';
import { LaravelZonesService } from '../../commons/services/laravel/laravel-zones.service';
import { NewZonesComponent } from '../../modules/home/modules/zones/components/new-zones/new-zones.component';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';
import { getTenants } from '../selectors/tenant.selectors';

@Injectable()
export class ZonesEffects {
    error$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(
                ZonesActions.deleteZoneFailed,
                ZonesActions.saveZoneFailed
            ),
            tap(({ error }) => {
              if (error) {
                this.alertService.showErrorMessage('Errore', error.message);
              }
            })
          ),
        { dispatch: false }
    );

    loadZones$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.loadZones),
          switchMap(({ page, perPage, order, direction, filters, includes }) => {
            return this.zonesService
              .list(page, perPage, order, direction, filters, includes)
              .pipe(
                map((result) =>
                    ZonesActions.loadZonesCompleted({
                      zones: result.data,
                      currentPage: page,
                      total: result.total,
                      perPage,
                      order,
                      direction,
                      filters,
                      includes
                  })
              ),
                catchError((error) =>
                  of(ZonesActions.loadZonesFailed({ error }))
                )
              );
          })
        )
    );

    changePage = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.changePage),
          withLatestFrom(this.store$.select(getZonesTableState)),
          map(
            ([
              { page, size },
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
                ZonesActions.loadZones({
                page: page,
                perPage: size,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    changeSort = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.changeSort),
          withLatestFrom(this.store$.select(getZonesTableState)),
          map(
            ([
              action,
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
                ZonesActions.loadZones({
                page: currentPage,
                perPage: perPage,
                order: action.order,
                direction: action.direction,
                filters,
                includes,
              })
          )
        )
    );

    changeFilters = createEffect(() =>
      this.actions$.pipe(
        ofType(ZonesActions.changeFilters),
        withLatestFrom(this.store$.select(getZonesTableState)),
        map(([{ filters }, { total, currentPage, perPage, direction, order, includes }]) => {
          // Verifica i filtri prima di inviarli
          //console.log('Filtri da inviare al server:', filters);

          const updatedFilters = {
            ...filters,
          };

          // Log dei filtri modificati
          //console.log('Filtri aggiornati per la richiesta:', updatedFilters);

          return ZonesActions.loadZones({
            page: currentPage,
            perPage: perPage,
            order,
            direction,
            filters: updatedFilters,
            includes
          });
        })
      )
    );



editZone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ZonesActions.editZone),
      withLatestFrom(this.store$.select(getTenants)),
      map(([action, tenants]) => {
        let dialogRef = this.dialog.open(NewZonesComponent, {
          data: { zone: action.zone, tenants },
          panelClass: 'full-screen-dialog'
        });
        return ZonesActions.zonesDialogOpened({ dialogId: dialogRef.id });
      })
    )
  );

    saveZone$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ZonesActions.saveZone),
        mergeMap(({ zones }) =>
          this.zonesService.upsert(zones.toDTO()).pipe(
            map((result) =>
              ZonesActions.saveZoneCompleted({ zone: result })
            ),
            catchError((error) =>
              of(ZonesActions.saveZoneFailed({ error }))
            )
          )
        )
      )
    );

    // // Se la validazione passa, effettua la chiamata API
    // proceedWithSaveZone$ = createEffect(() =>
    //     this.actions$.pipe(
    //       ofType(ZonesActions.proceedWithSaveZone),
    //       mergeMap(({ zones }) =>
    //         this.zonesService.upsert(zones.toDTO()).pipe(
    //           map((result) =>
    //             ZonesActions.saveZoneCompleted({ zone: result })
    //           ),
    //           catchError((error) =>
    //             of(ZonesActions.saveZoneFailed({ error }))
    //           )
    //         )
    //       )
    //     )
    // );


    onSaveCompleted$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.saveZoneCompleted),
          map((action) => action.zone),
          tap((zones) =>
            this.alertService.showConfirmMessage(
              `Zona con id ${zones.id} salvata con successo`
            )
          ),
          map(() => ZonesActions.closeZonesDialog())
        )
    );

    deleteZone$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.deleteZone),
          switchMap(({ zone }) =>
            this.alertService
              .showConfirmDialog(
                'Conferma eliminazione',
                `Sei sicuro di voler eliminare l'elemento selezionato?`
              )
              .pipe(
                mergeMap((confirm) => {
                  return confirm
                    ? this.zonesService.delete(zone.id).pipe(
                        map(() =>
                            ZonesActions.deleteZoneCompleted({
                              zone,
                          })
                        ),
                        catchError((error) =>
                          of(ZonesActions.deleteZoneFailed({ error }))
                        )
                      )
                    : of(ZonesActions.deleteZoneCancelled());
                })
              )
          )
        )
    );

    onDeleteCompleted$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.deleteZoneCompleted),
          tap(({ zone }) =>
            this.alertService.showConfirmMessage(
              `Zona con id ${zone.id} disattivata con successo`
            )
          ),
          map(() => ZonesActions.closeZonesDialog())
        )
    );

    closeDialog = createEffect(
        () =>
          this.actions$.pipe(
            ofType(ZonesActions.closeZonesDialog),
            withLatestFrom(this.store$.select(getZoneDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
    );

    reloadAfterSave = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.saveZoneCompleted),
          withLatestFrom(this.store$.select(getZonesTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                ZonesActions.loadZones({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    reloadAfterDelete = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.deleteZoneCompleted),
          withLatestFrom(this.store$.select(getZonesTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                ZonesActions.loadZones({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );


    closeSelectionDialog = createEffect(
        () =>
          this.actions$.pipe(
            ofType(ZonesActions.closeSelectionDialog),
            withLatestFrom(this.store$.select(getSelectionDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
    );


    requestCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.requestCloseZonesDialog),
          switchMap(() => {
            const confirmDialogRef = this.dialog.open(DialogComponent, {
              disableClose: false
            });

            confirmDialogRef.componentInstance.title = "Chiudi";
            confirmDialogRef.componentInstance.confirmMessage =
              "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";

            return confirmDialogRef.afterClosed().pipe(
              map(result =>
                result
                  ? ZonesActions.confirmCloseZonesDialog()
                  : ZonesActions.cancelCloseZonesDialog()
              )
            );
          })
        )
      );


      confirmCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.confirmCloseZonesDialog),
          map(() => ZonesActions.closeZonesDialog())
        )
      );

      cancelCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ZonesActions.cancelCloseZonesDialog),
          map(() => {
            return { type: 'NO_ACTION' };
          })
        )
      );

      closeDialog$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(ZonesActions.closeZonesDialog),
            withLatestFrom(this.store$.select(getZoneDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
      );


    constructor(
        private actions$: Actions,
        private store$: Store<AppState>,
        private zonesService: LaravelZonesService,
        private dialog: MatDialog,
        private alertService: AlertService
    ) {}

}
